import { useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { subscriptionGateway } from '../index';
import { notifySubscriptionChanged } from '../subscriptionChanged';
import { formatCents, type CompanySubscriptionResponse, type PlanSummary } from '../types';
import { startOptionsFor } from './startOptions';
import { ApiError } from '../../identity/types';
import { billingErrorMessage, BILLING_UNAVAILABLE_MESSAGE, isPermissionDenied } from './billingErrors';
import { HOSTED_REDIRECT_ERROR, useHostedRedirect } from './hostedRedirect';

/** Approved Commercial V1: the 30-day trial grants Plus features. The server enforces this; the UI says so. */
const TRIAL_PLAN_CODE = 'PlusPayroll';

/**
 * EPN-105 — the explicit "Start free trial" / "Subscribe" choice for one plan.
 *  - Start free trial: 30 days, no credit card, never converts or charges automatically. Calls `POST /api/billing/trial`.
 *  - Subscribe: sends the user to the provider's hosted checkout (card or US bank account), where taxes are shown
 *    before they pay. Calls `POST /api/billing/checkout-sessions` and follows the returned URL. The return page
 *    never trusts the URL it is opened with; it asks the server (see CheckoutCompletePage).
 * Prices come from the plan the API returned, never from constants here.
 */
export function PlanStartPanel({
  plan, subscription, providerAvailable, onReadOnly, onCancel,
}: {
  plan: PlanSummary;
  subscription: CompanySubscriptionResponse | null;
  providerAvailable: boolean;
  /** Called when the server answers a plain 403, so the host page can switch to its read-only view. */
  onReadOnly?: () => void;
  onCancel?: () => void;
}) {
  const navigate = useNavigate();
  const redirect = useHostedRedirect();
  const options = startOptionsFor(subscription);
  const [busy, setBusy] = useState<'trial' | 'subscribe' | null>(null);
  const [error, setError] = useState<string | null>(null);
  // One key per intent, reused if the outcome is unknown (network failure / 5xx) so a retry cannot double-start; a
  // definite refusal (4xx) clears it so the next attempt is genuinely new.
  const trialKey = useRef<string | null>(null);
  const checkoutKey = useRef<string | null>(null);

  function settle(err: unknown, key: { current: string | null }): void {
    if (err instanceof ApiError && err.status < 500) key.current = null;
    if (isPermissionDenied(err)) onReadOnly?.();
    setError(billingErrorMessage(err, 'Something went wrong. Nothing was changed.'));
  }

  async function startTrial() {
    setBusy('trial');
    setError(null);
    trialKey.current ??= crypto.randomUUID();
    try {
      await subscriptionGateway.startTrial({ planCode: TRIAL_PLAN_CODE, idempotencyKey: trialKey.current });
      trialKey.current = null;
      notifySubscriptionChanged();
      navigate('/settings/subscription/confirmation', { state: { changeKind: 'first-selection' } });
    } catch (err) {
      settle(err, trialKey);
    } finally {
      setBusy(null);
    }
  }

  async function subscribe() {
    setBusy('subscribe');
    setError(null);
    checkoutKey.current ??= crypto.randomUUID();
    try {
      const session = await subscriptionGateway.createCheckoutSession({ planCode: plan.code, idempotencyKey: checkoutKey.current });
      try {
        redirect(session.url);
      } catch {
        setError(HOSTED_REDIRECT_ERROR);
      }
    } catch (err) {
      settle(err, checkoutKey);
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="plan-start" role="group" aria-label={`Start ${plan.name}`} data-testid="plan-start-panel">
      <h4>{plan.name}</h4>
      <p>
        {formatCents(plan.baseMonthlyFeeCents)} per month plus {formatCents(plan.perEmployeeMonthlyFeeCents)} per billable
        employee. Taxes are shown before you pay.
      </p>

      {!providerAvailable && (
        <p className="billing-notice billing-notice--warning" role="status">
          <span aria-hidden="true">⚠</span> {BILLING_UNAVAILABLE_MESSAGE} Subscribing is not possible here, so nothing was changed.
        </p>
      )}

      {error && <p className="error" role="alert">{error}</p>}

      {options.trial && (
        <section className="plan-start__option" aria-label="Free trial">
          <h5>Free trial</h5>
          <p>
            30 days with Plus features (the free trial always includes Plus, whichever plan you are looking at), no credit card
            needed. We will never charge you automatically: when the trial ends, you decide which plan to subscribe to.
          </p>
          <p className="muted">
            During the trial, payroll runs are simulated. Live direct deposits, tax payments and filings stay off until you
            subscribe and your account is verified.
          </p>
          <button type="button" disabled={busy !== null} onClick={() => void startTrial()}>
            {busy === 'trial' ? 'Starting your trial…' : 'Start free trial'}
          </button>
        </section>
      )}

      {options.subscribe && (
        <section className="plan-start__option" aria-label="Subscribe">
          <h5>Subscribe</h5>
          <p>
            You will go to Stripe&apos;s secure checkout to pay by card or US bank account. You will see taxes before you pay.
            This covers your Escochex software subscription only. You can cancel any time; it ends at the end of the billing period.
          </p>
          <button type="button" disabled={busy !== null || !providerAvailable} onClick={() => void subscribe()}>
            {busy === 'subscribe' ? 'Opening secure checkout…' : `Subscribe to ${plan.name}`}
          </button>
        </section>
      )}

      {options.changePlan && (
        <p>
          You already have a subscription. To switch to {plan.name}, review the exact charge first on the{' '}
          <Link to={`/settings/billing?changePlan=${encodeURIComponent(plan.code)}`}>billing page</Link>.
        </p>
      )}

      {options.fixPaymentFirst && (
        <p>
          Your last payment did not go through, so plan changes are paused. <Link to="/settings/billing">Update your payment method</Link> first.
        </p>
      )}

      {onCancel && (
        <button type="button" className="link" onClick={onCancel}>Close</button>
      )}
    </div>
  );
}
